/* eslint-disable camelcase */
import { toast } from '@postscript/components';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { RESPONSES_QUERY_KEYS } from 'components/responses/constants';
import { api } from 'controllers/network/apiClient';
import { ResponsesAPI } from '../withResponses';
import { CLASSIFICATIONS_BASE_URL } from './useClassifications';
import { FEEDBACK_QUERY_KEY } from './useFeedback';

export interface Response {
  classification: string | null;
  customer: string;
  incoming_message_id: number;
  opted_out: boolean;
  received_time: string;
  response: string;
  subscriber_id: number;
  total_unresolved_messages: number;
  sentiment?: string | null;
  resolved_at?: string;
  customer_id?: number;
  previous_message_type_id?: string;
  phone_number?: string;
  previous_message_type?: string;
  shop_name?: string;
  shop_id?: number;
  new_prospect?: boolean;
  data: any;
}

export interface GetResponsesParams {
  includeSubscribers: boolean;
  includeOptedOutSubscriber: boolean;
  limit?: number;
  start?: number;
  unresolved?: boolean;
  resolved?: boolean;
  classification?: string;
  previous_message_type_id?: string;
  previous_message_type?: string;
  sentiment?: string[];
  filter_last_30?: boolean;
  assigned?: boolean;
  cross_shop?: boolean;
  include_keywords?: boolean;
  tags?: string;
  lifecycle?: string;
  sort_order?: 'asc' | 'desc';
}

export const listResponses = async (
  params: GetResponsesParams | null,
): Promise<ResponsesAPI> => {
  const {
    includeSubscribers,
    includeOptedOutSubscriber,
    sentiment,
    sort_order,
    ...rest
  } = params || ({} as GetResponsesParams);
  const queryString: { [key: string]: string | boolean | number | string[] } = {
    ...rest,
    include_subscribers: includeSubscribers,
    include_opted_out_subscribers: includeOptedOutSubscriber,
  };

  if (sort_order) {
    queryString.sort_order = sort_order;
  }

  const urlParams = new URLSearchParams();
  sentiment?.forEach((entry) => urlParams.append('sentiment', entry));
  Object.keys(queryString).forEach((entry) =>
    urlParams.append(entry, String(queryString[entry])),
  );
  const data = await api.get(`/responses/grouped?${urlParams.toString()}`);
  return {
    ...data,
    start: urlParams.get('start') || 0,
    limit: urlParams.get('limit') || 0,
  };
};

export const useResponses = (
  params: GetResponsesParams | null,
  queryKey?: string[],
  options: UseQueryOptions<ResponsesAPI> = {},
): UseQueryResult<ResponsesAPI> => {
  return useQuery<ResponsesAPI>({
    queryKey: !queryKey ? [RESPONSES_QUERY_KEYS.RESPONSES, params] : queryKey,
    queryFn: () => listResponses(params),
    enabled: !!params && Object.keys(params).length > 0,
    staleTime: 1_000 * 60,
    ...options,
  });
};

const deleteClassification = async (messageId: number) =>
  api.delete(`${CLASSIFICATIONS_BASE_URL}/${messageId}`);

export const useRemoveMessageClassification = (): UseMutationResult<
  void,
  any,
  number
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: number) => deleteClassification(messageId),
    onSuccess: (_, messageId: number) => {
      queryClient.setQueriesData<ResponsesAPI>(
        [RESPONSES_QUERY_KEYS.RESPONSES],
        (oldData) => {
          if (!oldData) {
            return { success: true, data: [], totalResults: 0 };
          }

          return {
            ...oldData,
            data: oldData.data.map((response) =>
              response.incoming_message_id !== messageId
                ? response
                : {
                    ...response,
                    classification: null,
                  },
            ),
          };
        },
      );

      queryClient.invalidateQueries({
        queryKey: [RESPONSES_QUERY_KEYS.RESPONSES],
        refetchType: 'none',
      });
    },
  });
};

/* eslint-disable camelcase */
interface ResponseFeedbackParams {
  messageId: number;
  feedback_type: string;
  message: string;
  subscriberId: number;
  responseType?: 'shopper' | 'sender';
  messageGuid?: string;
  message_text?: string;
  data?: Record<string, unknown>;
  feedbackValue?: string;
  feedbackMetadata?: Record<string, unknown>;
  // Targets another shop's message (admin/sales agents viewing a shop's
  // conversation). Sent as a query param — `current_shop_id` on the backend
  // reads it and verifies sales authorization. Omit for merchants.
  shopId?: number;
}

const addResponseFeedback = async (params: ResponseFeedbackParams) => {
  const { responseType = 'shopper' } = params;
  const shopParam = params.shopId != null ? `?shop_id=${params.shopId}` : '';

  if (responseType === 'shopper') {
    return api.post(`/v2/shopper/response-feedback${shopParam}`, {
      message_id: params.messageId,
      feedback_type: params.feedback_type,
      message: params.message,
      subscriber_id: params.subscriberId,
      ...(params.message_text && { message_text: params.message_text }),
      ...(params.data && { data: params.data }),
    });
  }

  if (!params.messageGuid) {
    throw new Error('Message GUID is required for sender feedback');
  }
  return api.post(
    `/v2/sender/message/${params.messageGuid}/feedback/${shopParam}`,
    {
      feedback: params.feedback_type.toUpperCase(),
      ...(params.feedbackValue && { feedbackValue: params.feedbackValue }),
      ...(params.feedbackMetadata && {
        feedbackMetadata: params.feedbackMetadata,
      }),
      ...(params.message_text && { messageText: params.message_text }),
    },
  );
};
/* eslint-enable camelcase */

export const useAddResponseFeedback = (): UseMutationResult<
  void,
  any,
  ResponseFeedbackParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: ResponseFeedbackParams) => addResponseFeedback(params),
    onSuccess: () => {
      toast.success('Feedback submitted');
      queryClient.invalidateQueries({ queryKey: [FEEDBACK_QUERY_KEY] });
    },
    onError: () => {
      toast.error('Failed to submit feedback');
    },
  });
};
